import { SimplePagination, Table } from '@dixa/lib-shared-habla';
import type { PhoneNumber } from 'lib-shared-legacy-types';
import { useCallback, useState } from 'react';

import EditNumberModal from 'components/settings/containers/sections/telephony/editNumberModal';
import type { WithPhoneNumbersInjectedProps } from 'hocs';

import NewFlowModal from './modals/newFlowModal';
import OutboundNumberModal from './modals/outboundNumberModal';
import ReleaseNumberModal from './modals/releaseNumberModal';
import styles from './numberTable.module.scss';
import NumberTableProvider from './numberTableContext';
import NumberTableItem from './numberTableItem';

const COLUMN_COUNT = 4;

type Props = WithPhoneNumbersInjectedProps;

enum ModalType {
  ReleaseNumberModal,
  OutboundNumberModal,
  EditNumberModal,
  NewFlowModal,
}

export const NumberTable = ({ phoneNumbers }: Props): JSX.Element => {
  const [selectedNumber, setSelectedNumber] = useState<PhoneNumber | null>(
    null,
  );
  const [openModalType, setOpenModalType] = useState<ModalType | null>(null);
  const {
    items,
    getItems,
    getItemsAsyncStatus,
    previousPage,
    nextPage,
    hasPreviousPage,
    hasNextPage,
  } = phoneNumbers;

  const isLoading = getItemsAsyncStatus === 'loading';
  const isFailed = getItemsAsyncStatus === 'failed';

  const hideModal = useCallback(() => {
    setOpenModalType(null);
    setSelectedNumber(null);
  }, []);

  const createOpenModalHandler = useCallback(
    (modalType: ModalType) => (selectedNumber: PhoneNumber) => {
      setOpenModalType(modalType);
      setSelectedNumber(selectedNumber);
    },
    [],
  );

  return (
    <>
      <NumberTableProvider
        fetchNumbers={getItems}
        showChangeOutbound={createOpenModalHandler(
          ModalType.OutboundNumberModal,
        )}
        showReleaseNumber={createOpenModalHandler(ModalType.ReleaseNumberModal)}
        showEditNumber={createOpenModalHandler(ModalType.EditNumberModal)}
        showNewFlow={createOpenModalHandler(ModalType.NewFlowModal)}
      >
        <Table
          expandableRowsProps={{ expandableRows: true }}
          loading={isLoading}
          loadingFailed={isFailed}
          onLoadingRetry={getItems}
          stickyHeader
          stickyFooter
          headerWithBackground
        >
          <Table.Thead>
            <Table.Tr>
              <Table.Th width="50%">Phone number</Table.Th>
              <Table.Th>Name</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>

          <Table.Tbody>
            {items.map((number) => (
              <NumberTableItem key={number.e164_format} number={number} />
            ))}
          </Table.Tbody>

          <Table.Tfoot>
            <Table.Tr className={styles.foot}>
              <Table.Td colSpan={COLUMN_COUNT}>
                <SimplePagination
                  previousButtonClick={previousPage}
                  previousButtonDisabled={!hasPreviousPage}
                  nextButtonClick={nextPage}
                  nextButtonDisabled={!hasNextPage}
                  loading={isLoading}
                />
              </Table.Td>
            </Table.Tr>
          </Table.Tfoot>
        </Table>
      </NumberTableProvider>

      <ReleaseNumberModal
        isShown={openModalType === ModalType.ReleaseNumberModal}
        number={selectedNumber}
        onHide={hideModal}
        phoneNumbers={phoneNumbers}
      />
      <OutboundNumberModal
        isShown={openModalType === ModalType.OutboundNumberModal}
        number={selectedNumber}
        onHide={hideModal}
        phoneNumbers={phoneNumbers}
      />
      <EditNumberModal
        isShown={openModalType === ModalType.EditNumberModal}
        selectedNumber={selectedNumber}
        hideEditNumberModal={hideModal}
        showEditNumberModal={createOpenModalHandler(ModalType.EditNumberModal)}
        fetchNumbers={getItems}
      />
      <NewFlowModal
        number={selectedNumber}
        isShown={openModalType === ModalType.NewFlowModal}
        onClose={hideModal}
      />
    </>
  );
};
